/**
 * Accessible ARIA Tabs (automatic activation).
 *
 * This function:
 * - Manages ARIA state only (`aria-selected`, `aria-hidden`)
 * - Updates `tabindex` on tabs
 * - Supports keyboard navigation
 * - Leaves all visual hiding/showing and animation of panels to user-supplied CSS
 *
 * Tabs are expected to be <button> elements, so native Enter/Space
 * behavior will trigger activation via click.
 *
 * @param {HTMLElement} root
 *   The root element containing the tablist, tabs, and tabpanels.
 *   All queries are scoped to this element.
 *
 * @example
 * // JavaScript
 * initTabs(document.querySelector('.tabs'));
 *
 * @example
 * <!-- Expected markup -->
 * <div class="tabs">
 *   <div role="tablist" aria-orientation="horizontal">
 *     <button
 *       id="tab-1"
 *       role="tab"
 *       aria-selected="true"
 *       aria-controls="panel-1">
 *       Tab One
 *     </button>
 *     <button
 *       id="tab-2"
 *       role="tab"
 *       aria-selected="false"
 *       aria-controls="panel-2"
 *       tabindex="-1">
 *       Tab Two
 *     </button>
 *   </div>
 *
 *   <div
 *     id="panel-1"
 *     role="tabpanel"
 *     aria-labelledby="tab-1">
 *     Panel one content
 *   </div>
 *
 *   <div
 *     id="panel-2"
 *     role="tabpanel"
 *     aria-labelledby="tab-2">
 *     Panel two content
 *   </div>
 * </div>
 *
 * @example
 * // Example CSS-driven visibility / animation
 * [role="tabpanel"][aria-hidden="true"] {
 *   display: none;
 * }
 *
 * @example
 * // Example animated approach
 * [role="tabpanel"] {
 *   transition: opacity 200ms ease;
 * }
 *
 * [role="tabpanel"][aria-hidden="true"] {
 *   opacity: 0;
 *   pointer-events: none;
 * }
 *
 * @notes
 * - Panels MUST be descendants of `root`.
 * - Each tab MUST have an `aria-controls` attribute pointing to its panel ID.
 * - Each panel SHOULD have `aria-labelledby` pointing back to its tab.
 * - If panels are visually hidden via opacity/transform, ensure focusable
 *   descendants are not reachable when `aria-hidden="true"`.
 *
 */
export default function initTabs(root) {
  const tablist = root.querySelector('[role="tablist"]');
  const tabs = [...root.querySelectorAll('[role="tab"]')];

  if (!tablist || tabs.length === 0) {
    console.error("Error initiating tabs. Missing required elements.");
    return;
  }

  const orientation = tablist.getAttribute("aria-orientation") || "horizontal";
  const firstTab = tabs[0];
  const lastTab = tabs[tabs.length - 1];
  const tabToPanel = new Map(); // Maps tab to corresponding panel

  const setSelectedTab = (selectedTab) => {
    if (!tabToPanel.has(selectedTab)) return;

    if (selectedTab.getAttribute("aria-selected") === "true") return;

    for (const tab of tabs) {
      const isSelected = tab === selectedTab;
      const panel = tabToPanel.get(tab);
      if (!panel) continue;

      tab.setAttribute("aria-selected", String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      panel.setAttribute("aria-hidden", String(!isSelected));
    }
  };

  const selectPreviousTab = (tab) => {
    if (tab === firstTab) {
      setSelectedTab(lastTab);
      lastTab.focus();
    } else {
      setSelectedTab(tabs[tabs.indexOf(tab) - 1]);
      tabs[tabs.indexOf(tab) - 1].focus();
    }
  };

  const selectNextTab = (tab) => {
    if (tab === lastTab) {
      setSelectedTab(firstTab);
      firstTab.focus();
    } else {
      setSelectedTab(tabs[tabs.indexOf(tab) + 1]);
      tabs[tabs.indexOf(tab) + 1].focus();
    }
  };

  const handleKeydown = (event) => {
    const target = event.currentTarget;

    switch (event.key) {
      case "ArrowLeft":
        if (orientation !== "horizontal") return;
        selectPreviousTab(target);
        event.preventDefault();
        break;

      case "ArrowRight":
        if (orientation !== "horizontal") return;
        selectNextTab(target);
        event.preventDefault();
        break;

      case "ArrowUp":
        if (orientation !== "vertical") return;
        selectPreviousTab(target);
        event.preventDefault();
        break;

      case "ArrowDown":
        if (orientation !== "vertical") return;
        selectNextTab(target);
        event.preventDefault();
        break;

      case "Home":
        setSelectedTab(firstTab);
        firstTab.focus();
        event.preventDefault();
        break;

      case "End":
        setSelectedTab(lastTab);
        lastTab.focus();
        event.preventDefault();
        break;
    }
  };

  const handleClick = (event) => {
    setSelectedTab(event.currentTarget);
  };

  // Initialize tabs
  let initialTab = null;
  for (const tab of tabs) {
    const panelId = tab.getAttribute("aria-controls");
    if (!panelId) {
      console.warn("No aria-controls on tab:", tab);
      continue;
    }

    const panel = root.querySelector(`#${panelId}`);
    if (!panel) {
      console.warn("No panel found for tab:", tab);
      continue;
    }

    tabToPanel.set(tab, panel);

    tab.addEventListener("keydown", handleKeydown);
    tab.addEventListener("click", handleClick);

    if (!initialTab) {
      initialTab = tab; // first valid fallback
    }

    if (tab.getAttribute("aria-selected") === "true") {
      initialTab = tab; // author-selected wins
    }
  }

  if (!initialTab) {
    console.error("Error initiating tabs. No valid tab/panel pairs found.");
    return;
  }

  setSelectedTab(initialTab);
}
